import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, Signal, signal, viewChild } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { I18NextPipe } from 'angular-i18next';
import { isEqual, isNil } from 'lodash';
import {
  ButtonComponent,
  FormGroupComponent,
  InputComponent,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  ModalSettings,
  SelectComponent
} from 'mbs-ui-kit';
import { BetaFeatureRequestComponent } from 'mbs-ui-shared@components/beta-feature-request/beta-feature-request.component';
import { ComputerListSelectComponent } from 'mbs-ui-shared@components/paged-list-select/computer-list-select.component';
import { CompanySelectComponent } from 'mbs-ui-shared@components/paged-select/company-select.component';
import { Company } from 'mbs-ui-shared@models';
import Computer, { GetComputersParams, OsType } from 'mbs-ui-shared@models/Computer';
import { IterationCtrlButtonModule } from 'mbs-ui-shared@modules/iteration-ctrl-button/iteration-ctrl-button.module';
import { debounceTime, filter, noop, pairwise, startWith } from 'rxjs';
import { ModalComputersData } from 'wizards/onboarding/steps/try-rmm/installation-mode/installationMode.constants';
import { getOSTypes, OSTypeModel } from './select-computers-modal.constants';

@UntilDestroy()
@Component({
  selector: 'mbs-select-computers-modal',
  templateUrl: './select-computers-modal.component.html',
  styleUrls: ['./select-computers-modal.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    IterationCtrlButtonModule,
    ComputerListSelectComponent,
    BetaFeatureRequestComponent,
    CompanySelectComponent,
    I18NextPipe,
    ModalComponent,
    InputComponent,
    SelectComponent,
    ModalFooterDirective,
    FormGroupComponent
  ]
})
export class SelectComputersModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  protected i18N = inject(I18NextPipe);
  private modalService = inject(ModalService);

  protected modal = viewChild(ModalComponent);
  protected selectComputers = viewChild(ComputerListSelectComponent);
  protected osTypes = getOSTypes(this.i18N);
  protected hiddenFilters = signal<GetComputersParams>(null);
  protected filter = signal<{ filter: GetComputersParams; initial: boolean }>(null);
  protected preSelectedCompanyIds: string[] = [];
  protected hiddenElements: { companySelect?: boolean; osSelect?: boolean } = {};
  protected computedFilters: Signal<{ filter: GetComputersParams; initial: boolean }> = computed(() => {
    const filter = this.filter();
    return { filter: { ...filter?.filter, ...this.hiddenFilters() }, initial: filter?.initial };
  });
  protected showBetaFeatureRequest = computed(() => {
    const selectComputers = this.selectComputers();

    return (
      !!this.data?.filter?.minAppVersion &&
      !!this.data?.betaFeatureName &&
      !!selectComputers?.filterDataLoaded() &&
      !selectComputers.isLoading() &&
      !selectComputers.hasVisibleItems()
    );
  });

  private readonly errorModalSettings: ModalSettings = {
    header: { title: this.i18N.transform('shared:computers.invalidSelectedComputer') },
    footer: { okButton: { show: false } }
  };

  public data: ModalComputersData;
  public multipleSelect = true;
  public notSetResult = false;

  public computerValidator: (comp: Computer) => boolean;
  public computerValidatorError: string;
  public computersNotFound: string;

  protected form = this.fb.group({
    computers: this.fb.control<Computer[]>([], { nonNullable: true }),
    filter: this.fb.group({
      search: this.fb.control<string>('', { nonNullable: true }),
      companies: this.fb.control<Company[]>([], { nonNullable: true }),
      os: this.fb.control<OSTypeModel[]>([], { nonNullable: true })
    })
  });

  private get computersControl() {
    return this.form.get('computers');
  }

  private get filterForm() {
    return this.form.get('filter');
  }

  ngOnInit() {
    if (!isNil(this.data.multipleSelect)) this.multipleSelect = this.data.multipleSelect;

    !this.data?.selectedAll && this.data?.items && this.computersControl.patchValue(this.data.items);
    this.data?.selectedAll && this.selectComputers()?.selectAllInitially.set(true);
    this.hiddenElements = { ...this.hiddenElements, ...(this.data?.hiddenElements || {}) };

    let search = false;

    this.hiddenFilters.set(this.data?.hiddenFilters);

    if (this.data?.filter?.search) {
      this.filterForm.get('search').patchValue(this.data.filter.search as string);
      search = true;
    }
    if (this.data?.filter?.companyIds) {
      this.preSelectedCompanyIds = [...(this.data.filter.companyIds as string[])];
      search = true;

      // If element is hidden, form value will be empty.
      if (this.hiddenElements.companySelect) {
        this.filterForm.get('companies').setValue(this.preSelectedCompanyIds.map((id) => ({ id }) as unknown as Company));
      }
    }
    if (this.data?.filter?.os) {
      this.filterForm.get('os').patchValue(this.osTypes.filter((os) => ((this.data?.filter?.os as OsType[]) || []).includes(os.id)));
      search = true;
    }

    if (this.data?.disableOs) {
      this.filterForm.get('os').disable();
    }
    this.notSetResult = this.data?.notSetResult;

    this.computerValidator = this.data?.computerValidator;
    this.computerValidatorError = this.data?.computerValidatorError;
    this.computersNotFound = this.data?.computersNotFound;

    search && this.handleSearch(this.data?.filter);

    this.filterForm.valueChanges
      .pipe(
        startWith(this.filterForm.value),
        pairwise(),
        filter(([prev, current]) => prev && !isEqual(prev, current)),
        debounceTime(500),
        untilDestroyed(this)
      )
      .subscribe(() => this.handleSearch());
  }

  handleSearch(initial: GetComputersParams = null): void {
    let newFilter: GetComputersParams = null;

    if (isNil(initial)) {
      const searchValue = this.filterForm.get('search').value?.trim() || '';
      const companiesValue = (this.filterForm.get('companies').value || []).map((company: Company) => company.id);
      const osTypes = (this.filterForm.get('os').value || []).map((os) => os.id);
      if (searchValue || !!companiesValue.length || !!osTypes.length) {
        newFilter = {};
        searchValue && (newFilter = { ...newFilter, search: searchValue });
        companiesValue.length && (newFilter = { ...newFilter, companyIds: companiesValue });
        osTypes.length && (newFilter = { ...newFilter, os: osTypes });
      }
    } else {
      newFilter = initial;
    }

    if (isEqual(this.filter()?.filter || null, newFilter)) return;

    this.filter.set({ filter: newFilter, initial: !isNil(initial) });
  }

  handleSave(): void {
    if (this.computerValidator && this.computerValidatorError) {
      const someInvalid = this.selectComputers()
        .value()
        .some((c: Computer) => !this.computerValidator(c));

      if (someInvalid) {
        return void this.modalService.open(this.errorModalSettings, this.computerValidatorError).then(noop).catch(noop);
      }

      return this.modal().save({
        items: this.selectComputers().value(),
        filter: this.filter()?.filter || null,
        selectedAll: this.selectComputers().selectedAll()
      } as ModalComputersData);
    }

    this.modal().save({
      items: this.selectComputers().value(),
      filter: this.filter()?.filter || null,
      selectedAll: this.selectComputers().selectedAll()
    } as ModalComputersData);
  }

  handleCancel(): void {
    this.modal().close();
  }
}
